import React, { useState, useEffect } from "react";
import { useLanguage } from "../../Translations/LenguageContext";
import ErrorMessage from "../../Components/ErrorMessage";
import Spinner from "../../Components/Spinner";
import { useNavigate, useParams } from "react-router-dom";
import { VscAzure } from "react-icons/vsc";
import { FaAws } from "react-icons/fa";
import { SiGooglecloud } from "react-icons/si";
import PricingModal from "./PricingModal";
import azureRegions from "../../JSON/AzureRegions.json";
import continentFlags from "../../JSON/ContinentIcon.json";
import regionDisplayNames from "../../JSON/AzureDisplayNames.json";
import { TbGalaxy, TbInfinity } from "react-icons/tb";

const NewDatabaseNoSQL = () => {
  const { t } = useLanguage();
  const { project_id } = useParams();
  const navigate = useNavigate();
  const [err, setErr] = useState({ message: "", display: false });
  const user_id = sessionStorage.getItem("user_id");
  const [loading, setLoading] = useState(false);
  const [loadingPage, setLoadingPage] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState("flex");
  const [interval, setSubsInterval] = useState("monthly");
  const [provider, setProvider] = useState("azure");
  const [dbName, setDbName] = useState("");
  const [description, setDescription] = useState("");
  const [ops, setOps] = useState("");
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [selectedRegion, setRegion] = useState("italynorth");
  const [hasPayment, setPayments] = useState(true);
  const [flexAlreadyPresent, setFlexAlreadyPresent] = useState(false);

  const goBack = () => {
    navigate(`/project_panel/${project_id}/databases`);
  };

  const NewDb = async () => {
     setErr({
        message: "",
        display: false,
      });
      
    if (!dbName.trim()) {
      setErr({
        message: "Please provide a name for the database",
        display: true,
      });
      return;
    }

    if (dbName.startsWith("db-") || dbName.startsWith("db")) {
      setErr({
        message: 'The database name CANNOT include "db"',
        display: true,
      });
      return;
    }

    if (!description.trim()) {
      setErr({
        message: "Please provide a description for the database",
        display: true,
      });
      return;
    }

    setLoading(true);
    let data = {
      user_id: user_id,
      project_id: project_id,
      db_name: dbName,
      description: description,
      plan: selectedPlan,
      provider: provider,
      selectedRegion: selectedRegion,
      interval: interval,
      db_type: "nosql",
    };

    try {
      const response = await fetch("https://backend.erioon.com/create_db", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });
      setLoading(false);
      const result = await response.json();
      if (response.ok) {
        const db_id = result.db_id;
        navigate(`/project_panel/${project_id}/databases/${db_id}/data`);
      } else {
        console.error("Error creating db:", response.statusText);
        setErr({
          message: result.error,
          display: true,
      });
      }
    } catch (error) {
      setLoading(false);
      setErr({
        message: error,
        display: true,
      });
    }
  };

  const fetchUserData = async () => {
    setLoadingPage(true);
    try {
      const response = await fetch(
        "https://backend.erioon.com/get_specific_data",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ user_id: user_id }),
        }
      );
      setLoadingPage(false);
      if (!response.ok) {
        throw new Error("Failed to fetch user data");
      }

      const data = await response.json();
      const payment_method = data.payment_method;

      if (payment_method) {
        setPayments(true);
      } else {
        setPayments(false);
      }
    } catch (error) {
      setTimeout(() => {
        setLoadingPage(false);
      }, 800);
    }
  };

  const plans = {
    flex: {
      description:
        "Erioon NoSQL is engineered for high availability and limitless scale, so you only pay for what you use, when you use it.",
      storage: "100MB Storage",
      backup_storage: "100MB Backup",
      ops: ops,
      backup: true,
      autoscale: true,
      price: 0,
      extra_gb: "$1 per GB",
      reads_price: "10.000/month",
      writes_price: "10.000/month",
    },
  };


  useEffect(() => {
    fetchUserData();
  }, [provider, selectedPlan]);

  if (loadingPage) {
    return (
      <div className="loading_new_db_cont">
        <div className="loading_resources_new fade-in">
          <div className="loading_resources_new_main"></div>
          <div className="loading_resources_new_main"></div>
        </div>
        <div className="loading_resume_new fade-in">
          <div className="loading_resume_new_main"></div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="new_section_main_view fade-in">
        <p className="new_section_main_view_title">
          <i className="fa-solid fa-chevron-left" onClick={goBack}></i> New
          database - NoSQL
        </p>
        <p className="new_section_main_view_subtitle">
          Complete all the fields and create the database
        </p>

        {!hasPayment && selectedPlan != "flex" && (
          <div className="no_payment_method_db fade-in">
            <p>
              To proceed, please add a valid payment method to your account.
            </p>
            <button
              className="add_payment_btn"
              onClick={() => navigate("/control_panel/billing")}
            >
              <i className="fa-solid fa-plus"></i> Add payment method
            </button>
          </div>
        )}

        <div className="new_section_main_input_cont fade-in">
          <div className="new_section_main_input_container">
            <p className="db_title">Choose a location</p>
            <div className="region_container">
              <div className="region_block">
                {["North America", "South America", "Australia"].map(
                  (continent) =>
                    azureRegions[continent] ? (
                      <div key={continent} className="continent_region">
                        <p className="continent_title_region">
                          {continentFlags[continent]} {continent}
                        </p>
                        <div className="reg_cont_main">
                          {azureRegions[continent].map((region) => (
                            <div
                              key={region}
                              value={region}
                              className={
                                selectedRegion === region
                                  ? "single_region_selected"
                                  : "single_region"
                              }
                              onClick={() => setRegion(region)}
                            >
                              {regionDisplayNames[region] || region}
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null
                )}
              </div>

              <div className="region_block">
                {["Europe", "Africa"].map((continent) =>
                  azureRegions[continent] ? (
                    <div key={continent} className="continent_region">
                      <p className="continent_title_region">
                        {continentFlags[continent]} {continent}
                      </p>
                      <div className="reg_cont_main">
                        {azureRegions[continent].map((region) => (
                          <div
                            key={region}
                            value={region}
                            className={
                              selectedRegion === region
                                ? "single_region_selected"
                                : "single_region"
                            }
                            onClick={() => setRegion(region)}
                          >
                            {regionDisplayNames[region] || region}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null
                )}
              </div>

              <div className="region_block">
                {["Asia", "Middle East"].map((continent) =>
                  azureRegions[continent] ? (
                    <div key={continent} className="continent_region">
                      <p className="continent_title_region">
                        {continentFlags[continent]} {continent}
                      </p>
                      <div className="reg_cont_main">
                        {azureRegions[continent].map((region) => (
                          <div
                            key={region}
                            value={region}
                            className={
                              selectedRegion === region
                                ? "single_region_selected"
                                : "single_region"
                            }
                            onClick={() => setRegion(region)}
                          >
                            {regionDisplayNames[region] || region}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null
                )}
              </div>
            </div>
          </div>
          <div className="cost_preview_container">
            <div className="cost_preview_cont">
              <p className="new_section_main_input_hint_title">
                Pay-per-use
                <span
                  className="price_info"
                  onClick={() => setShowPricingModal(true)}
                >
                  <i className="fa-solid fa-info"></i>
                </span>
              </p>
              <p className="new_section_main_input_hint_description">
                From small beginnings to seamless growth.
              </p>
            </div>
            <div className="preview_cont_db">
              <div className="review_cont">
                <p className="review_cont_title">
                  Erioon NoSQL engine
                </p>
                <p className="review_cont_data">
                  {" "}
                  <span>{plans[selectedPlan].description}</span>
                </p>
                <p className="review_cont_data">
                  Storage capacity<span>Start with 1GB</span>
                </p>
                <p className="review_cont_data">
                  Backup{" "}
                  <i className="fa-solid fa-check"></i>
                </p>
                <p className="review_cont_data">
                  Auto-scaling{" "}
                  <i
                    className={`fa-solid fa-${
                      plans[selectedPlan].autoscale ? "check" : "x"
                    }`}
                  ></i>
                </p>

                <div className="label_input_section_db">
                  <label>Database name *</label>
                  <input
                    value={dbName}
                    onChange={(e) => setDbName(e.target.value)}
                    placeholder="Main database"
                  />
                </div>
                <div className="label_input_section_db">
                  <label>Description *</label>
                  <input
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Datasets of customers and products..."
                    className="description_area_input"
                  />
                </div>
              </div>
              <ErrorMessage err={err} />
              <button
                disabled={(!hasPayment && selectedPlan !== "flex") || loading}
                className={`save_btn_db ${
                  !hasPayment && selectedPlan !== "flex"
                    ? "save_btn_db_disabled"
                    : ""
                }`}
                onClick={NewDb}
              >
                {loading ? (
                  <Spinner loading={loading} />
                ) : (
                  <>
                    <i className="fa-solid fa-plus"></i> Create database
                  </>
                )}
              </button>
            </div>
          </div>
          <PricingModal
            show={showPricingModal}
            handleClose={() => setShowPricingModal(false)}
            pricing_data={""}
            db_type={"noSQL"}
          />
        </div>
      </div>
    </div>
  );
};

export default NewDatabaseNoSQL;
